import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getProduct } from '../utils/products';
import type { Product, Variant } from '../types/product';
type CartLine = {
  slug: string;
  variantId: string;
  qty: number;
};
export type ResolvedLine = CartLine & {
  product: Product;
  variant: Variant;
  total: number;
};
type CartContextValue = {
  lines: ResolvedLine[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  add: (slug: string, variantId: string, qty?: number, openDrawer?: boolean) => void;
  setQty: (slug: string, variantId: string, qty: number) => void;
  remove: (slug: string, variantId: string) => void;
  clear: () => void;
  open: () => void;
  close: () => void;
};
const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = 'amira-cart';
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [raw, setRaw] = useState<CartLine[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as CartLine[];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
  }, [raw]);
  const add = useCallback((slug: string, variantId: string, qty = 1, openDrawer = true) => {
    setRaw(prev => {
      const existing = prev.find(l => l.slug === slug && l.variantId === variantId);
      if (existing) return prev.map(l => l === existing ? {
        ...l,
        qty: Math.min(l.qty + qty, 20)
      } : l);
      return [...prev, {
        slug,
        variantId,
        qty
      }];
    });
    if (openDrawer) setIsOpen(true);
  }, []);
  const setQty = useCallback((slug: string, variantId: string, qty: number) => {
    setRaw(prev => qty <= 0 ? prev.filter(l => !(l.slug === slug && l.variantId === variantId)) : prev.map(l => l.slug === slug && l.variantId === variantId ? {
      ...l,
      qty: Math.min(qty, 20)
    } : l));
  }, []);
  const remove = useCallback((slug: string, variantId: string) => {
    setRaw(prev => prev.filter(l => !(l.slug === slug && l.variantId === variantId)));
  }, []);
  const value = useMemo<CartContextValue>(() => {
    const lines = raw.flatMap(l => {
      const product = getProduct(l.slug);
      const variant = product?.variants.find(v => v.id === l.variantId);
      return product && variant ? [{
        ...l,
        product,
        variant,
        total: variant.price * l.qty
      }] : [];
    });
    return {
      lines,
      count: lines.reduce((s, l) => s + l.qty, 0),
      subtotal: lines.reduce((s, l) => s + l.total, 0),
      isOpen,
      add,
      setQty,
      remove,
      clear: () => setRaw([]),
      open: () => setIsOpen(true),
      close: () => setIsOpen(false)
    };
  }, [raw, isOpen, add, setQty, remove]);
  return <CartContext.Provider value={value} data-id="element-368" data-mp="b37mm3f558uu">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}